import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PeriodChips, Totals, Bars, spendStyle, p } from './SpendingParts';

const API = process.env.REACT_APP_BACKEND_URL;

// Settings → what the models cost: everything together, by area (translation,
// companions, writing…) and by model, for the last day, week, month or ever.

export default function Spending() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${API}/spending?token=${token}`)
      .then(res => setData(res.data))
      .catch(() => setErrorMsg("couldn't add it up — try again"));
  }, []);

  const since = data?.tracked_since ? new Date(data.tracked_since).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' }) : null;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{spendStyle}</style>
      <header style={s.header}>
        <button className="sp-btn" style={s.back} onClick={() => navigate('/settings')} aria-label="back">←</button>
        <div style={s.title}>what the models cost</div>
      </header>

      <main style={s.main}>
        {errorMsg && <div style={p.error}>{errorMsg}</div>}
        {!data && !errorMsg && <div style={p.empty}>adding it up…</div>}
        {data && (
          <>
            <section style={s.section}>
              <Totals total={data.total} period={period} />
              <PeriodChips value={period} onChange={setPeriod} />
            </section>

            <section style={s.section}>
              <div style={s.secTitle}>by area</div>
              <Bars period={period} empty="nothing spent in this period"
                rows={data.areas.map(a => ({ ...a, key: a.key }))} />
            </section>

            <section style={s.section}>
              <div style={s.secTitle}>by model</div>
              <Bars period={period} empty="nothing spent in this period"
                rows={data.models.map(m => ({ ...m, key: m.model }))} />
            </section>

            <div style={s.note}>
              translation and video have always kept their costs, so their figures go all the way back.
              companions, the writing room, study line ideas and reading notes are counted
              {since ? ` from ${since}` : ' from when this was added'} — before that, their costs weren't recorded.
              voices (ElevenLabs) are billed separately and aren't included here.
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  main: { maxWidth: '680px', margin: '0 auto', padding: '22px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px' },
  section: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '20px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' },
  secTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontStyle: 'italic' },
  note: { fontSize: '12px', color: '#6d5d4e', lineHeight: 1.65, padding: '0 4px' },
};
